import { Vector } from '../shared/vector.ts';

import type { ControlPoint, Entity, Player } from '../shared/entities.ts';
import { TAU } from '../shared/math.ts';
import {
  CAPTURE_THRESHOLD,
  DASH_COOLDOWN,
  MAP_SIZE,
} from '../shared/parameters.ts';
import { WEAPONS } from '../shared/weapons.ts';
import type { World } from '../shared/world.ts';

const ARENA_COLOR = '#2b2b2b';
const GRID_COLOR = '#333';
const GRID_SIZE = 200;
const AIM_LENGTH = 75;
const NAME_OFFSET = 20;
/** Straight up. Both waits fill from here, one down each side. */
const TOP = -TAU / 4;

type PositionOf = (entity: Entity) => Vector;

/** Draws the world, centred on whoever is playing here. */
export function createRenderer(canvas: HTMLCanvasElement) {
  const context = getContext(canvas);

  resize();
  window.addEventListener('resize', resize);

  return { render };

  function resize() {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }

  function render(world: World, positionOf: PositionOf, center: Vector) {
    const offset = Vector.of(canvas.width / 2, canvas.height / 2).sub(center);

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.translate(Math.round(offset.x), Math.round(offset.y));

    drawArena(context);

    for (const entity of world.entities.values()) {
      drawEntity(context, entity, positionOf(entity));
    }

    context.restore();
  }
}

function getContext(canvas: HTMLCanvasElement) {
  const context = canvas.getContext('2d');

  if (!context) throw new Error('This browser has no 2D canvas.');

  return context;
}

function drawArena(context: CanvasRenderingContext2D) {
  context.fillStyle = ARENA_COLOR;
  context.fillRect(0, 0, MAP_SIZE, MAP_SIZE);

  context.strokeStyle = GRID_COLOR;
  context.lineWidth = 1;
  context.beginPath();

  for (let at = GRID_SIZE; at < MAP_SIZE; at += GRID_SIZE) {
    context.moveTo(at, 0);
    context.lineTo(at, MAP_SIZE);
    context.moveTo(0, at);
    context.lineTo(MAP_SIZE, at);
  }

  context.stroke();
}

function drawEntity(
  context: CanvasRenderingContext2D,
  entity: Entity,
  at: Vector,
) {
  if (entity.kind === 'bullet') {
    circle(context, at, entity.size, entity.team);
    return;
  }

  if (entity.kind === 'control-point') {
    drawControlPoint(context, entity, at);
    return;
  }

  drawPlayer(context, entity, at);
}

function drawControlPoint(
  context: CanvasRenderingContext2D,
  point: ControlPoint,
  at: Vector,
) {
  circle(context, at, point.size, point.ownerTeam ?? '#3a3a3a');

  if (!point.capturingTeam) return;

  const captured = (point.capture / CAPTURE_THRESHOLD) * TAU;

  context.beginPath();
  context.arc(at.x, at.y, point.size + 4, 0, captured);
  context.strokeStyle = point.capturingTeam;
  context.lineWidth = 4;
  context.stroke();
}

function drawPlayer(
  context: CanvasRenderingContext2D,
  player: Player,
  at: Vector,
) {
  const aim = player.aim.isZero ? Vector.of(1, 0) : player.aim;
  const tip = at.add(aim.setMagnitude(AIM_LENGTH));

  context.beginPath();
  context.moveTo(at.x, at.y);
  context.lineTo(tip.x, tip.y);
  context.strokeStyle = 'rgba(255, 255, 255, 0.25)';
  context.lineWidth = 1;
  context.stroke();

  circle(context, at, player.size, player.team);

  context.font = '12px system-ui, sans-serif';
  context.textAlign = 'center';
  context.fillStyle = '#ddd';
  context.fillText(player.name, at.x, at.y + player.size + NAME_OFFSET);

  const weapon = WEAPONS[player.weapon];

  drawWait(context, player, at, 'left', player.reload / weapon.reloadTime);
  drawWait(context, player, at, 'right', player.dashCooldown / DASH_COOLDOWN);
}

/**
 * Where a wait's arc runs: from the top, down one half of the ring, as far as
 * the fraction still left says. Canvas angles grow clockwise, because y points
 * down — so the left half is the counterclockwise one, and getting that flag
 * wrong is what puts a reload on the dash's side.
 */
export function waitArc(side: 'left' | 'right', left: number) {
  const counterclockwise = side === 'left';
  const sweep = Math.min(1, Math.max(0, left)) * (TAU / 2);

  return {
    start: TOP,
    end: counterclockwise ? TOP - sweep : TOP + sweep,
    counterclockwise,
  };
}

/**
 * The two things a player can be waiting for, one down each half of a ring:
 * reloading on the left, the dash on the right. Both drain from the top, so a
 * bare circle means everything is ready — and which half is lit is what says
 * which wait it is, because the colour cannot: the colour is the team.
 */
function drawWait(
  context: CanvasRenderingContext2D,
  player: Player,
  at: Vector,
  side: 'left' | 'right',
  left: number,
) {
  if (left <= 0) return;

  const { start, end, counterclockwise } = waitArc(side, left);

  context.beginPath();
  context.arc(at.x, at.y, player.size * 1.6, start, end, counterclockwise);
  context.strokeStyle = player.team;
  context.lineWidth = 2;
  context.stroke();
}

function circle(
  context: CanvasRenderingContext2D,
  at: Vector,
  radius: number,
  color: string,
) {
  context.beginPath();
  context.arc(at.x, at.y, radius, 0, TAU);
  context.fillStyle = color;
  context.fill();
}
